// Shared chrome for auth screens — TopBar + centered card.
//
// Each auth screen renders <AuthLayout title="…" subtitle="…">{form}</AuthLayout>
// and stays focused on the form itself.

import type { ReactNode } from "react";

import { IconButton, Surface, TopBar, Wordmark } from "@/design-system";
import { useBranding } from "@/lib/branding";

export interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onBack?: () => void;
  footer?: ReactNode;
}

export function AuthLayout({
  title,
  subtitle,
  children,
  onBack,
  footer,
}: AuthLayoutProps) {
  const { displayName } = useBranding();
  return (
    <Surface flex>
      <TopBar
        left={
          <div className="flex items-center gap-2.5">
            {onBack && (
              <IconButton ariaLabel="Indietro" onClick={onBack}>
                <svg
                  width="10"
                  height="16"
                  viewBox="0 0 10 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <path d="M8 2L2 8l6 6" />
                </svg>
              </IconButton>
            )}
            <Wordmark size={15} displayName={displayName} />
          </div>
        }
      />
      <div className="ics-noscroll flex-1 min-h-0 overflow-y-auto">
        <div className="mx-auto w-full max-w-[440px] px-6 pt-6 pb-10">
          <header className="mb-6">
            <h1 className="font-serif text-[28px] m-0 font-medium tracking-[-0.4px] text-ink">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1.5 text-[14px] leading-[1.5] text-ink-muted">
                {subtitle}
              </p>
            )}
          </header>
          {children}
          {footer && (
            <footer className="mt-6 text-center text-[13px] text-ink-muted">
              {footer}
            </footer>
          )}
        </div>
      </div>
    </Surface>
  );
}
